import React from 'react';

export type LoadingType = 'spinner' | 'skeleton' | 'progress' | 'pulse';
export type LoadingSize = 'sm' | 'md' | 'lg';

interface LoadingIndicatorProps {
  type?: LoadingType;
  size?: LoadingSize;
  message?: string;
  progress?: number;
  className?: string;
  'aria-label'?: string;
}

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  type = 'spinner',
  size = 'md',
  message,
  progress,
  className = '',
  'aria-label': ariaLabel
}) => {
  const getSizeClasses = (size: LoadingSize) => {
    switch (size) {
      case 'sm':
        return 'w-4 h-4';
      case 'lg':
        return 'w-8 h-8';
      default:
        return 'w-6 h-6';
    }
  };

  const getTextSizeClasses = (size: LoadingSize) => {
    switch (size) {
      case 'sm':
        return 'text-xs';
      case 'lg':
        return 'text-base';
      default:
        return 'text-sm';
    }
  };

  const renderSpinner = () => (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`
          ${getSizeClasses(size)} 
          animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400
          ${className}
        `}
        role="status"
        aria-label={ariaLabel || 'Loading'}
      />
      {message && (
        <p className={`text-gray-600 dark:text-gray-400 ${getTextSizeClasses(size)}`}>
          {message}
        </p>
      )}
    </div>
  );

  const renderSkeleton = () => (
    <div className={`animate-pulse ${className}`} role="status" aria-label={ariaLabel || 'Loading content'}>
      <div className="space-y-3">
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6"></div>
      </div>
      {message && (
        <p className={`mt-2 text-gray-600 dark:text-gray-400 ${getTextSizeClasses(size)}`}>
          {message}
        </p>
      )}
    </div>
  );

  const renderProgress = () => (
    <div className={`w-full ${className}`} role="status" aria-label={ariaLabel || 'Loading progress'}>
      <div className="flex items-center justify-between mb-1">
        {message && (
          <span className={`text-gray-700 dark:text-gray-300 ${getTextSizeClasses(size)}`}>
            {message}
          </span>
        )}
        {typeof progress === 'number' && (
          <span className={`text-gray-500 dark:text-gray-400 ${getTextSizeClasses(size)}`}>
            {Math.round(progress)}%
          </span>
        )}
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${typeof progress === 'number' ? progress : 0}%` }}
          aria-valuenow={typeof progress === 'number' ? progress : 0}
          aria-valuemin={0}
          aria-valuemax={100}
          role="progressbar"
        />
      </div>
    </div>
  );

  const renderPulse = () => (
    <div className={`flex items-center gap-2 ${className}`} role="status" aria-label={ariaLabel || 'Loading'}>
      <div className={`${getSizeClasses(size)} bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse`} />
      <div className={`${getSizeClasses(size)} bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }} />
      <div className={`${getSizeClasses(size)} bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse`} style={{ animationDelay: '0.4s' }} />
      {message && (
        <p className={`ml-2 text-gray-600 dark:text-gray-400 ${getTextSizeClasses(size)}`}>
          {message}
        </p>
      )}
    </div>
  );

  switch (type) {
    case 'skeleton':
      return renderSkeleton();
    case 'progress':
      return renderProgress();
    case 'pulse':
      return renderPulse();
    default:
      return renderSpinner();
  }
};